import { Link } from 'react-router-dom';
import { Activity, ShieldCheck, HeadphonesIcon, Server } from 'lucide-react';

const CDN = 'https://cdn.prod.website-files.com/602f5458a0b1d868c6c84d5b';

const leftCards = [
  {
    icon: Server,
    title: 'Deploy in your environment',
    description:
      'Run Nanonets on-prem or your private VPC - ideal for strict security, compliance, data residency.',
  },
  {
    icon: HeadphonesIcon,
    title: 'Enterprise support and SLAs',
    description:
      'Hands-on forward deployed support and tailored SLAs to meet your enterprise needs.',
  },
];

const rightCards = [
  {
    icon: ShieldCheck,
    title: 'SOC2, HIPAA, GDPR, ISO 27001',
    description:
      'Enterprise-grade security, certified for sensitive and regulated data. View our security policies here.',
  },
  {
    icon: Activity,
    title: '99.9%+ uptime',
    description:
      'Battle-tested infrastructure you can trust in production and at scale.',
  }
];

const logos = [
  { src: `${CDN}/618cf7d27e8255e6af866c7b_Roche.svg`, alt: 'Roche' },
  { src: `${CDN}/6910cf070dc7f93e800af8be_Ryanair%201.svg`, alt: 'Ryanair' },
  { src: `${CDN}/6910cf0d2562409136668c19_Publicis_logo%201.svg`, alt: 'Publicis' },
  { src: `${CDN}/6910cf14a66ae9bf48ba573d_Schneider_Electric_2007%201.svg`, alt: 'Schneider Electric' },
  { src: `${process.env.PUBLIC_URL}/pg-BXHMeVKW.png`, alt: 'P&G', raw: true },
  { src: `${CDN}/64d22435a83397855c43ab85_PMKuwait.svg`, alt: 'PM Kuwait' },
  { src: `${CDN}/6910cc55979af174280b9f3e_Group%20(2).svg`, alt: 'Enterprise client' },
  { src: `${CDN}/6910cd2d43a9203e7ece72e2_g43.svg`, alt: 'Enterprise client' },
];

/* ── Inline SVG lock illustration ── */
function LockIllustration() {
  return (
    <div className="relative w-full max-w-[340px] aspect-square mx-auto">
      {/* Grid background */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 340 340"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Subtle grid lines */}
        {Array.from({ length: 9 }).map((_, i) => (
          <line
            key={`h-${i}`}
            x1="40"
            y1={70 + i * 25}
            x2="300"
            y2={70 + i * 25}
            stroke="rgba(84,111,255,0.08)"
            strokeWidth="0.5"
          />
        ))}
        {Array.from({ length: 9 }).map((_, i) => (
          <line
            key={`v-${i}`}
            x1={70 + i * 25}
            y1="40"
            x2={70 + i * 25}
            y2="300"
            stroke="rgba(84,111,255,0.08)"
            strokeWidth="0.5"
          />
        ))}

        {/* Lock body (rounded rect) */}
        <rect
          x="95"
          y="140"
          width="150"
          height="120"
          rx="12"
          fill="none"
          stroke="rgba(84,111,255,0.5)"
          strokeWidth="2.5"
        />
        {/* Inner square grid in lock body */}
        {Array.from({ length: 5 }).map((_, i) => (
          <line
            key={`lb-h-${i}`}
            x1="95"
            y1={165 + i * 20}
            x2="245"
            y2={165 + i * 20}
            stroke="rgba(84,111,255,0.08)"
            strokeWidth="0.5"
          />
        ))}
        {Array.from({ length: 5 }).map((_, i) => (
          <line
            key={`lb-v-${i}`}
            x1={125 + i * 25}
            y1="140"
            x2={125 + i * 25}
            y2="260"
            stroke="rgba(84,111,255,0.08)"
            strokeWidth="0.5"
          />
        ))}

        {/* Lock shackle (arc) */}
        <path
          d="M130 140 L130 110 C130 80 210 80 210 110 L210 140"
          fill="none"
          stroke="rgba(84,111,255,0.5)"
          strokeWidth="2.5"
        />

        {/* Keyhole */}
        <circle cx="170" cy="195" r="12" fill="none" stroke="rgba(84,111,255,0.55)" strokeWidth="2.5" />
        <line x1="170" y1="207" x2="170" y2="225" stroke="rgba(84,111,255,0.55)" strokeWidth="2.5" />
      </svg>

      {/* Decorative colored dots */}
      <div className="absolute" style={{ top: '8%', left: '38%' }}>
        <div className="w-2.5 h-2.5 rounded-sm bg-[#546FFF] opacity-60" />
      </div>
      <div className="absolute" style={{ top: '18%', right: '12%' }}>
        <div className="w-2 h-2 rounded-sm bg-[#818CF8] opacity-60" />
      </div>
      <div className="absolute" style={{ top: '32%', left: '8%' }}>
        <div className="w-2 h-2 rounded-sm bg-[#546FFF] opacity-50" />
      </div>
      <div className="absolute" style={{ top: '55%', left: '5%' }}>
        <div className="w-1.5 h-1.5 rounded-sm bg-[#A78BFA] opacity-50" />
      </div>
      <div className="absolute" style={{ bottom: '18%', left: '35%' }}>
        <div className="w-2.5 h-2.5 rounded-sm bg-[#546FFF] opacity-50" />
      </div>
      <div className="absolute" style={{ bottom: '8%', left: '48%' }}>
        <div className="w-2 h-2 rounded-sm bg-[#A78BFA] opacity-50" />
      </div>
      <div className="absolute" style={{ bottom: '28%', right: '15%' }}>
        <div className="w-2 h-2 rounded-sm bg-[#546FFF] opacity-40" />
      </div>
      <div className="absolute" style={{ bottom: '12%', left: '52%' }}>
        <div className="w-1.5 h-1.5 rounded-sm bg-gray-500 opacity-40" />
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#1a1f35] flex items-center justify-center flex-shrink-0 border border-[#2a2f45]">
        <Icon className="w-4 h-4 text-[#546FFF]" strokeWidth={1.5} />
      </div>
      <h3 className="text-white font-semibold text-[15px]">{title}</h3>
      <p className="text-[#9CA3AF] text-sm leading-relaxed">{description}</p>
    </div>
  );
}

export default function EnterpriseReady() {
  return (
    <section className="py-24" style={{ background: '#0f1117' }}>
      <div className="max-w-[1200px] mx-auto px-6">
        {/* ── Header ── */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-[42px] font-bold text-white mb-4 leading-tight">
            Enterprise-ready
          </h2>
          <p className="text-[#9CA3AF] text-base max-w-[460px] mx-auto mb-7 leading-relaxed">
            Security, scale, support to meet the demands of your most demanding production pipelines.
          </p>
          <Link
            to="/demo"
            className="inline-flex items-center justify-center px-6 py-2.5 bg-white text-[#546FFF] rounded-lg font-medium text-sm hover:bg-[#F0F3FF] transition-colors duration-200"
          >
            Contact sales
          </Link>
        </div>

        {/* ── Features + Lock illustration ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 items-center mb-20">
          {/* Left features */}
          <div className="flex flex-col gap-12">
            {leftCards.map((card, i) => (
              <FeatureCard key={i} {...card} />
            ))}
          </div>

          {/* Center lock graphic */}
          <div className="flex items-center justify-center">
            <LockIllustration />
          </div>

          {/* Right features */}
          <div className="flex flex-col gap-12">
            {rightCards.map((card, i) => (
              <FeatureCard key={i} {...card} />
            ))}
          </div>
        </div>

        {/* ── Logo trust strip ── */}
        <div className="border-t border-[#1e2235] pt-10">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <p className="text-[#6B7280] text-xs tracking-wide whitespace-nowrap">
              Trusted by the world's
              <br />
              leading enterprises.
            </p>
            <div className="flex items-center gap-10 md:gap-14 flex-wrap justify-center">
              {logos.map((logo, i) => (
                <img
                  key={i}
                  src={logo.src}
                  alt={logo.alt}
                  loading="lazy"
                  decoding="async"
                  className={`h-[26px] md:h-8 w-auto max-w-[120px] md:max-w-[150px] object-contain opacity-40 hover:opacity-70 transition-opacity duration-300 ${logo.raw ? '' : 'brightness-0 invert'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
